/**
 * Which sidecar build is answering?
 *
 * The agent service runs the source baked into its Docker image, so a container
 * that was not rebuilt keeps running an older build — and drops request fields it
 * predates WITHOUT SAYING SO. The editor then pins a block, sees the chip, and
 * gets "which block do you want to modify?" back, with nothing anywhere pointing
 * at the cause.
 *
 * So we ask the sidecar what it understands and compare it with what we send.
 * Diagnostics only: a failed check never blocks the chat.
 */

const ENDPOINT = '/api/builder/chat/health'

// Request fields this frontend sends that a sidecar has to know about for the
// corresponding feature to work at all. Keep in step with the payload built in
// `useBuilderChatStream.send`.
export const REQUIRED_FEATURES = {
  blockContext: "l'assistant répondra sans savoir quel bloc est ciblé",
  // Le bloc ciblé voyage sur le MESSAGE de chaque tour. Une version antérieure
  // le posait sur le system prompt, que le SDK relit depuis le transcript à la
  // reprise : le contexte se perdait dès la 2ᵉ question — et l'assistant CRÉAIT
  // un bloc au lieu de modifier celui qu'on regardait.
  pinnedBlockTurn: "l'assistant perdra le bloc ciblé dès la deuxième question, et pourra créer un bloc au lieu de le modifier",
  blockCreateSite: 'le bloc créé ne sera rattaché à aucun site, et ne réapparaîtra donc pas dans ce catalogue',
  // Celle-ci se lit à l'envers des autres : son absence est le cas NORMAL en
  // prod et sur Upsun, où le système de fichiers est monté en lecture seule.
  // Le panneau ne la réclame donc pas — il masque la case à cocher, et n'avertit
  // que si on l'interroge explicitement.
  fileWrite: "ce service n'ouvre pas l'écriture de fichiers : la case resterait sans effet",
}

/** @returns {Promise<{reachable:boolean, features:string[]}|null>} */
export async function fetchSidecarHealth(signal) {
  try {
    const res = await fetch(ENDPOINT, { credentials: 'include', cache: 'no-store', signal })
    if (!res.ok) return null
    return await res.json()
  }
  catch {
    return null
  }
}

/**
 * The sentence to show when the running sidecar cannot honour `feature`, or
 * `null` when it can (or when we could not find out — never cry wolf on a failed
 * check).
 *
 * A reachable sidecar reporting NO features at all is one older than the health
 * marker itself: that is precisely the stale case worth naming.
 */
export function staleSidecarWarning(health, feature) {
  if (!health?.reachable) return null
  if (Array.isArray(health.features) && health.features.includes(feature)) return null
  const consequence = REQUIRED_FEATURES[feature] ?? 'la fonctionnalité sera ignorée'
  return `L'assistant tourne une version antérieure à cette fonctionnalité : ${consequence}. `
    + 'Rechargez le service (`rebuild-agent`).'
}

/**
 * Le nom lisible du modèle qui répond, dérivé de son identifiant.
 *
 * Le panneau annonçait « Sonnet 4.6 » en dur : la variable d'environnement du
 * service pouvait dire tout autre chose, et l'éditeur lisait un nom faux sans
 * aucun moyen de s'en apercevoir. C'est le service qui dit désormais lequel il
 * fait tourner (`/health`), et cette fonction se contente de le mettre en forme.
 *
 * DÉRIVÉ, jamais une table : un identifiant inconnu s'affiche tel quel plutôt
 * que d'être traduit de travers, et un modèle sorti après ce code se nomme tout
 * seul. La forme reconnue est `[éditeur.]claude-<famille>-<version…>[-<date>]` —
 * `claude-sonnet-4-6` → « Sonnet 4.6 », `claude-opus-5` → « Opus 5 »,
 * `claude-haiku-4-5-20251001` → « Haiku 4.5 » (le millésime ne se lit pas).
 *
 * @param {string|null|undefined} id
 * @returns {string|null} `null` quand on ne sait pas — l'appelant n'affiche
 *   alors rien, plutôt qu'un nom inventé.
 */
export function modelLabel(id) {
  if (typeof id !== 'string' || !id.trim()) return null
  // `anthropic.claude-…` sur Bedrock, `claude-…@date` sur Vertex, et le
  // millésime en suffixe — retiré AVANT la lecture de la version, qui le
  // prendrait sinon pour un chiffre de plus (« Haiku 4.5.20251001 »).
  const nu = id.trim()
    .replace(/^[a-z0-9_-]+\./, '')
    .split('@')[0]
    .replace(/-\d{8}$/, '')
  const match = /^claude-([a-z]+)-((?:\d+-)*\d+)$/.exec(nu)
  if (!match) return nu
  const [, famille, version] = match
  return `${famille.charAt(0).toUpperCase()}${famille.slice(1)} ${version.replace(/-/g, '.')}`
}
